import React, { useState, useEffect } from 'react';
import { Heart, Loader2, Search, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchFavorites();
    }
  }, [isAuthenticated]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/user/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (activityId) => {
    setRemoving(activityId);
    try {
      await axios.delete(`${API_URL}/api/user/favorites/${activityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(favorites.filter(f => f.id !== activityId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    } finally {
      setRemoving(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50 flex items-center justify-center">
        <Card className="max-w-md border-4 border-teal-300">
          <CardContent className="p-8 text-center">
            <Heart className="h-16 w-16 text-teal-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Sign in to see your favorites</h2>
            <p className="text-gray-600 mb-6">Create an account to save your favorite activities!</p>
            <div className="space-y-3">
              <Link to="/login">
                <Button className="w-full bg-gradient-to-r from-blue-500 to-teal-500 text-white">
                  Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button variant="outline" className="w-full border-2 border-teal-400">
                  Create Account
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-teal-500 mx-auto mb-4" />
          <p className="text-xl text-gray-600">Loading your favorites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
              My Favorites
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto font-medium">
            Your saved activities for quality time with your kids
          </p>
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="h-24 w-24 text-gray-300 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">No favorites yet</h2>
            <p className="text-gray-600 mb-6">Start exploring activities and save your favorites!</p>
            <Link to="/activities">
              <Button className="bg-gradient-to-r from-blue-500 to-teal-500 text-white">
                <Search className="h-4 w-4 mr-2" />
                Browse Activities
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-6">{favorites.length} saved {favorites.length === 1 ? 'activity' : 'activities'}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {favorites.map((activity) => (
                <Card key={activity.id} className="border-4 border-teal-200 hover:border-teal-400 transition-all duration-300 hover:shadow-2xl overflow-hidden group">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={activity.imageUrl} 
                      alt={activity.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute top-3 right-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="bg-white/90 hover:bg-red-50 border-red-300 text-red-600"
                        onClick={() => removeFavorite(activity.id)}
                        disabled={removing === activity.id}
                      >
                        {removing === activity.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <div className="absolute top-3 left-3">
                      <Badge className="bg-gradient-to-r from-blue-500 to-teal-500 text-white font-bold">
                        {activity.ageGroup}
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {activity.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                      {activity.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="border-teal-300 text-teal-700">
                        {activity.category}
                      </Badge>
                      <Badge variant="outline" className="border-gray-300 text-gray-600">
                        {activity.duration}
                      </Badge>
                    </div>
                  </CardContent>
                  
                  <CardFooter className="p-6 pt-0">
                    <Link to={`/activity/${activity.id}`} className="w-full">
                      <Button className="w-full bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 hover:from-blue-600 hover:via-teal-600 hover:to-green-600 text-white font-bold shadow-lg">
                        View Details
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Favorites;
